import { CirclePause, CirclePlay, SlidersHorizontal, Tags, Trash2, type LucideIcon } from 'lucide-react';
import type { MenuAction } from '@platform/design-system';
import type { Product } from '../product.ts';
import { usePageContext } from '../../usePageContext.ts';

export type StatusChange = 'publish' | 'deactivate' | 'archive';

export const SELECTION_ONLY_ACTIONS: readonly string[] = ['publish', 'deactivate', 'print-labels'];

type StatusOption = {
  readonly change: StatusChange;
  readonly labelKey: string;
  readonly Icon: LucideIcon;
  readonly tone?: 'danger';
  readonly appliesTo: (product: Product) => boolean;
};

const PUBLISH: StatusOption = {
  change: 'publish',
  labelKey: 'productEditor.publish',
  Icon: CirclePlay,
  appliesTo: (product) => product.status !== 'active',
};

const DEACTIVATE: StatusOption = {
  change: 'deactivate',
  labelKey: 'productEditor.deactivate',
  Icon: CirclePause,
  appliesTo: (product) => product.status === 'active',
};

const ARCHIVE: StatusOption = {
  change: 'archive',
  labelKey: 'catalog.delete',
  Icon: Trash2,
  tone: 'danger',
  appliesTo: (product) => product.status !== 'archived',
};

export type ProductActionHandlers = {
  readonly busy: boolean;
  readonly canAdjustStock: boolean;
  readonly onStatus: (change: StatusChange, products: readonly Product[]) => void;
  readonly onAdjustStock: (products: readonly Product[]) => void;
  readonly onPrintLabels: (products: readonly Product[]) => void;
};

export const useProductActions = ({
  busy,
  canAdjustStock,
  onStatus,
  onAdjustStock,
  onPrintLabels,
}: ProductActionHandlers) => {
  const { t } = usePageContext();

  const statusAction = (option: StatusOption, products: readonly Product[]): MenuAction[] => {
    const targets = products.filter(option.appliesTo);
    if (targets.length === 0) return [];
    return [
      {
        key: option.change,
        label: t(option.labelKey),
        icon: <option.Icon size={16} aria-hidden="true" />,
        disabled: busy,
        ...(option.tone === undefined ? {} : { tone: option.tone }),
        onSelect: () => {
          onStatus(option.change, targets);
        },
      },
    ];
  };

  return (products: readonly Product[]): readonly MenuAction[] => [
    ...statusAction(PUBLISH, products),
    ...statusAction(DEACTIVATE, products),
    ...(canAdjustStock
      ? [
          {
            key: 'adjust-stock',
            label: t('adjustStock.title'),
            icon: <SlidersHorizontal size={16} aria-hidden="true" />,
            disabled: busy,
            onSelect: () => {
              onAdjustStock(products);
            },
          },
        ]
      : []),
    {
      key: 'print-labels',
      label: t('productList.printLabels'),
      icon: <Tags size={16} aria-hidden="true" />,
      disabled: busy,
      onSelect: () => {
        onPrintLabels(products);
      },
    },
    ...statusAction(ARCHIVE, products),
  ];
};
